import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import S3Uploader from "@/components/S3Uploader";
import { TrailerInspections } from "@/components/TrailerInspections";
import { ArrowLeft, Loader2, Truck, Users, Wrench, CalendarOff, ClipboardList, MapPin, HardHat, Plus, X, ChevronDown, Pencil, FileText, AlertTriangle } from "lucide-react";

function Info({ label, value }) {
  return <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div><div className="mt-0.5 text-sm text-slate-800">{value || "—"}</div></div>;
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManage = can("operations.manage") || can("deliveries.manage");
  const [v, setV] = useState(null);
  const [error, setError] = useState(false);
  const [crew, setCrew] = useState([]);
  const [staffOpts, setStaffOpts] = useState([]);
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setV(null); setError(false); load(); /* eslint-disable-next-line */ }, [id]);
  function load() { return api.get(`/vehicles/${id}`).then((r) => { setV(r.data); setCrew(r.data.crew || []); }).catch(() => setError(true)); }
  useEffect(() => { api.get(`/vehicles/meta/assignable-staff`).then((r) => setStaffOpts(r.data.items || [])).catch(() => {}); }, []);

  const addStaff = async () => {
    const s = staffOpts.find((x) => x.name === sel);
    if (!s) return;
    setSaving(true);
    try { const r = await api.post(`/vehicles/${id}/staff`, { name: s.name, role: s.role }); setCrew(r.data.crew); setSel(""); toast.success(`${s.name} assigned`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not assign"); }
    finally { setSaving(false); }
  };
  const removeStaff = async (name) => {
    try { const r = await api.delete(`/vehicles/${id}/staff`, { params: { name } }); setCrew(r.data.crew); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (error) return <div className="p-10 text-center" data-testid="vehicle-not-found"><p className="text-slate-500">Vehicle {id} not found.</p><Button variant="outline" className="mt-4" onClick={() => navigate("/plant-transport")}>Back</Button></div>;
  if (!v) return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading vehicle…</div>;

  // Monthly calendar (current month) marking days with shifts
  const today = new Date();
  const year = today.getFullYear(), month = today.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const shiftDays = new Set(v.shifts.map((s) => s.date).filter((d) => d.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`)).map((d) => parseInt(d.slice(8, 10), 10)));

  return (
    <div className="p-6 lg:p-8" data-testid="vehicle-detail">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-3 mb-6">
        <span className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center"><Truck className="w-6 h-6" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="vehicle-detail-title">{v.title}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="mono text-sm text-slate-600">{v.registration || "—"}</span>
            <Pill colour={v.availability === "Available" ? "green" : "red"}>{v.availability}</Pill>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
            <h2 className="text-base font-bold text-slate-900 mb-4">Vehicle Information</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
              <Info label="Description" value={v.description} />
              <Info label="Current Mileage" value={v.odometer} />
              <Info label="Purchase Date" value={v.purchased_date} />
              <Info label="Depot" value={v.depot} />
              <Info label="Department" value={v.department} />
              <Info label="Type" value={v.asset_type} />
            </div>
          </section>

          <TrailerInspections asset={id} canManage={canManage} isTrailer={v.is_trailer} onFlag={load} />

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="vehicle-calendar">
            <h2 className="text-base font-bold text-slate-900 mb-4">Monthly Job Calendar — {today.toLocaleString("en-GB", { month: "long", year: "numeric" })}</h2>
            <div className="grid grid-cols-7 gap-1 text-center">
              {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d} className="text-[11px] font-semibold text-slate-400 py-1">{d}</div>)}
              {Array.from({ length: startOffset }).map((_, i) => <div key={`e${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1, has = shiftDays.has(day);
                return <div key={day} className={`aspect-square flex items-center justify-center text-sm rounded-lg ${has ? "bg-indigo-600 text-white font-semibold" : "text-slate-600 bg-slate-50"}`}>{day}</div>;
              })}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="vehicle-upcoming">
            <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-indigo-500" /> Upcoming Jobs ({v.upcoming.length})</h2>
            {v.upcoming.length === 0 ? <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No upcoming jobs.</div> : (
              <div className="divide-y divide-slate-100">
                {v.upcoming.map((s) => (
                  <Link key={s.shift_id} to={`/tasks/${s.task_legacy_id}`} className="flex items-center justify-between py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded-md">
                    <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{s.task_legacy_id}: {s.task_title}</div><div className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.venue_postcode} · {s.date}</div></div>
                    {s.status && <Pill colour={s.status_colour}>{s.status}</Pill>}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="vehicle-crew">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><HardHat className="w-4 h-4 text-indigo-500" /> Remote Engineers & Technical</h2>
            {crew.length === 0 ? (
              <div className="text-sm text-slate-400 mb-3">No engineers or technical staff assigned.</div>
            ) : (
              <div className="space-y-2 mb-3">
                {crew.map((m) => (
                  <div key={m.name} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={`crew-member-${m.name}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{m.name}</div>
                      <div className="text-[11px] text-slate-400">{m.role}</div>
                    </div>
                    {canManage && <button onClick={() => removeStaff(m.name)} className="text-slate-300 hover:text-rose-500 shrink-0" data-testid={`crew-remove-${m.name}`}><X className="w-4 h-4" /></button>}
                  </div>
                ))}
              </div>
            )}
            {canManage ? (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="crew-select"
                    className="w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none">
                    <option value="">Select engineer or technical…</option>
                    {staffOpts.filter((s) => !crew.some((c) => c.name === s.name)).map((s) => <option key={s.name} value={s.name}>{s.name} — {s.role}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
                <Button onClick={addStaff} disabled={!sel || saving} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1" data-testid="crew-add">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
                </Button>
              </div>
            ) : <p className="text-[11px] text-slate-400">You do not have permission to change assignments.</p>}
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="vehicle-staff">
            <h2 className="text-base font-bold text-slate-900 mb-1 flex items-center gap-2"><Users className="w-4 h-4 text-purple-500" /> Assigned Employees</h2>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mb-3 inline-block">Demonstration assignment</p>
            {v.assignment ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Primary</span><span className="font-medium text-slate-800">{v.assignment.primary || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Additional</span><span className="font-medium text-slate-800 text-right">{(v.assignment.additional || []).join(", ") || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">From</span><span className="text-slate-700">{v.assignment.start_date}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">To</span><span className="text-slate-700">{v.assignment.end_date || "Ongoing"}</span></div>
              </div>
            ) : <div className="text-sm text-slate-400">No assignment.</div>}
          </section>

          <MaintenanceCard vehicleId={id} maintenance={v.maintenance} canManage={canManage} onChanged={load} />

          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="vehicle-unavailability">
            <h2 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2"><CalendarOff className="w-4 h-4 text-rose-500" /> Unavailability</h2>
            {v.unavailability.length === 0 ? <div className="text-sm text-slate-400">Fully available.</div> : v.unavailability.map((u, i) => (
              <div key={i} className="text-sm flex justify-between py-1"><span className="text-slate-700">{u.date}</span><span className="text-slate-500">{u.reason}</span></div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ st }) {
  if (!st || st.status === "unset") return <span className="text-[11px] text-slate-400">Not set</span>;
  const map = {
    overdue: ["bg-rose-50 text-rose-700 border-rose-200", "Overdue"],
    due_soon: ["bg-amber-50 text-amber-700 border-amber-200", "Due soon"],
    ok: ["bg-emerald-50 text-emerald-700 border-emerald-200", "OK"],
  };
  const [cls, label] = map[st.status] || map.ok;
  const d = st.days_left;
  const extra = d != null ? (d < 0 ? ` · ${Math.abs(d)}d ago` : ` · ${d}d`) : "";
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}{extra}</span>;
}

const DOC_CATS = ["MOT Certificate", "Service Record", "Other"];

function MaintenanceCard({ vehicleId, maintenance, canManage, onChanged }) {
  const m = maintenance || {};
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ mot_due: m.mot_due || "", next_service_due: m.next_service_due || "", last_service: m.last_service || "", mot_test_number: m.mot_test_number || "", service_mileage: m.service_mileage || "", garage_provider: m.garage_provider || "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ mot_due: m.mot_due || "", next_service_due: m.next_service_due || "", last_service: m.last_service || "", mot_test_number: m.mot_test_number || "", service_mileage: m.service_mileage || "", garage_provider: m.garage_provider || "" }); }, [m.mot_due, m.next_service_due, m.last_service, m.mot_test_number, m.service_mileage, m.garage_provider]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/vehicles/${vehicleId}/maintenance`, form); toast.success("Maintenance updated"); setEditing(false); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const view = async (d) => {
    try { const r = await api.get(`/files/${d.path}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Could not open document"); }
  };

  const docs = m.documents || [];
  const dateRow = (label, key, st) => (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="text-sm text-slate-500">{label}</span>
      {editing ? (
        <input type="date" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="rounded-lg border border-slate-200 px-2 py-1 text-sm" data-testid={`maint-input-${key}`} />
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-800">{form[key] || "—"}</span>
          {st && <StatusBadge st={st} />}
        </div>
      )}
    </div>
  );

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="vehicle-maintenance">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-500" /> MOT &amp; Service</h2>
        {canManage && (editing
          ? <div className="flex gap-1.5"><Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} disabled={saving} data-testid="maint-save">{saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save</Button></div>
          : <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditing(true)} data-testid="maint-edit"><Pencil className="w-3.5 h-3.5" /> Edit</Button>)}
      </div>
      <div className="divide-y divide-slate-100">
        {dateRow("MOT Due", "mot_due", m.mot_status)}
        {dateRow("Next Service Due", "next_service_due", m.service_status)}
        {dateRow("Last Service", "last_service", null)}
        {[["mot_test_number", "MOT Test Number", "e.g. 1234 5678 9012"], ["service_mileage", "Service Mileage", "e.g. 45210"], ["garage_provider", "Garage / Provider", "e.g. Kwik Fit Blackburn"]].map(([k, label, ph]) => (
          <div key={k} className="flex items-center justify-between gap-2 py-1.5">
            <span className="text-sm text-slate-500">{label}</span>
            {editing
              ? <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} className="rounded-lg border border-slate-200 px-2 py-1 text-sm w-44" data-testid={`maint-input-${k}`} />
              : <span className="text-sm text-slate-800">{form[k] || "—"}</span>}
          </div>
        ))}
      </div>
      {m.mot_doc_status && (m.mot_doc_status.status === "missing" || m.mot_doc_status.status === "stale") && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="mot-doc-warning">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {m.mot_doc_status.status === "missing"
            ? "No MOT certificate on file — upload the current certificate."
            : "The MOT certificate on file predates the current MOT — upload the latest certificate."}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-100">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Documents</h3>
        {docs.length > 0 && (
          <div className="space-y-1.5 mb-3">
            <p className="text-[11px] text-slate-400">Existing documents (read-only, pending migration to secure storage)</p>
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`vehicle-doc-${d.id}`}>
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <button onClick={() => view(d)} className="text-sm text-indigo-600 hover:underline truncate block max-w-full text-left" data-testid={`vehicle-doc-view-${d.id}`}>{d.filename}</button>
                  <div className="text-[11px] text-slate-400">{d.category} · {(d.size / 1024).toFixed(0)} KB · legacy</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <S3Uploader recordType="vehicles" recordId={vehicleId} categories={DOC_CATS} canManage={canManage} accept="image/*,application/pdf" serverSide />
      </div>
    </section>
  );
}
